// src/services/api.js
import axios from 'axios';

// Default Base API URL (used for initial setup)
const DEFAULT_API_BASE_URL = 'http://localhost:5000';
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL; // Export API_BASE_URL

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL, // Initialize with default or environment variable
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor to add ignoreAdultContent from localStorage and authorization token
apiClient.interceptors.request.use(config => {
  // Read the value from localStorage
  const storedValue = localStorage.getItem('ignoreAdultContent');
  // Parse it, default to true if not found or invalid JSON
  let ignoreAdult = true; // Default value
  if (storedValue !== null) {
    try {
      ignoreAdult = JSON.parse(storedValue);
    } catch (e) {
      console.error("Error parsing ignoreAdultContent from localStorage:", e);
      // Keep the default value true in case of parsing error
    }
  }

  // Ensure params object exists
  if (!config.params) {
    config.params = {};
  }

  // Add the parameter
  config.params.ignoreAdultContent = ignoreAdult;

  // Add debug mode from localStorage
  const isDebugMode = localStorage.getItem('isDebugMode');
  if (isDebugMode === 'true') {
    config.params.debug = 'true';
  }

  // Add authorization token if it exists
  const authToken = localStorage.getItem('authToken');
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }

  // Log the final params being sent (optional, for debugging)
  // console.debug('API Request Params:', config.params);

  return config;
}, error => {
  // Do something with request error
  return Promise.reject(error);
});

// Response interceptor to handle all session invalidation and expiry cases
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      const errorMessage = error.response.data?.error || '';
      localStorage.removeItem('authToken');
      // Use a distinct message for admin-forced logouts vs natural session expiry
      if (errorMessage.includes('Session invalidated')) {
        localStorage.setItem('panorama_login_message', 'Your session was terminated. Please sign in again.');
      } else {
        localStorage.setItem('panorama_login_message', 'You have been signed out due to inactivity. Please sign in again.');
      }
      window.location.href = '/login';
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);


// API service object with methods for different endpoints
const apiService = {
  /**
   * Get lineups by postal code
   * @param {string} postalCode - The postal code to search for lineups
   * @returns {Promise} - Promise that resolves to an array of lineups
   */
  getLineups: async (postalCode) => {
    try {
      console.log(`Fetching lineups for postal code: ${postalCode}`);
      const response = await apiClient.get(`/lineups?postalcode=${encodeURIComponent(postalCode)}`);
      console.log('API Response:', response);
      return response.data;
    } catch (error) {
      console.error('Lineups API error:', error);
      throw error;
    }
  },
  
  /**
   * Get lineups by MSO, City, or Lineup ID
   * @param {string} mso - The MSO, City, or Lineup ID to search for lineups
   * @returns {Promise} - Promise that resolves to an array of lineups
   */
  getLineupsByMso: async (mso) => {
    try {
      const response = await apiClient.get(`/lineups?mso=${encodeURIComponent(mso)}`);
      return response.data;
    } catch (error) {
      console.error('Lineups by MSO API error:', error);
      throw error;
    }
  },
  
  /**
   * Get lineup details by lineup ID
   * @param {string} lineupId - The lineup ID
   * @returns {Promise} - Promise that resolves to an array of channels in the lineup
   */
  getLineupDetails: async (lineupId) => {
    try {
      const response = await apiClient.get(`/lineup?lineupid=${encodeURIComponent(lineupId)}&limit=9999`);
      return response.data;
    } catch (error) {
      console.error('Lineup details API error:', error);
      throw error;
    }
  },
  
  /**
   * Get schedules by program service ID
   * @param {number} prgSvcId - The program service ID
   * @param {number} timezoneId - Optional timezone ID for time conversion (defaults to 0 for UTC)
   * @returns {Promise} - Promise that resolves to an array of schedule dates
   */
  getSchedulesByPrgSvcId: async (prgSvcId, timezoneId = 0) => {
    try {
      const params = new URLSearchParams();
      params.append('prgsvcid', prgSvcId);
      if (timezoneId) {
        params.append('timezone_id', timezoneId);
      }
      const response = await apiClient.get(`/schedules?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Schedules API error:', error);
      throw error;
    }
  },
  
  /**
   * Get schedule details by schedule ID
   * @param {string} scheduleId - The schedule ID
   * @param {number} timezoneId - Optional timezone ID for time conversion (defaults to 0 for UTC)
   * @returns {Promise} - Promise that resolves to an array of programs in the schedule
   */
  getScheduleDetails: async (scheduleId, timezoneId = 0) => {
    try {
      const params = new URLSearchParams();
      params.append('scheduleid', scheduleId);
      if (timezoneId) {
        params.append('timezone_id', timezoneId);
      }
      const response = await apiClient.get(`/schedule?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Schedule details API error:', error);
      throw error;
    }
  },
  
  /**
   * Search for programs, optionally with pagination.
   * @param {string} query - The search query string (minimum 2 characters)
   * @param {string} [programType] - Optional program type to filter results
   * @param {number} [page] - Optional page number for pagination (1-based)
   * @param {number} [limit] - Optional number of results per page
   * @param {number[]} [genreIds] - Optional array of genre IDs to filter by
   * @param {string[]} [descLangs] - Optional array of description languages to filter by
   * @param {string[]} [titleLangs] - Optional array of title languages to filter by
   * @param {boolean} [isCastMember] - Optional flag to search for cast members
   * @param {boolean} [isCrewMember] - Optional flag to search for crew members
   * @param {boolean} [isDebug] - Optional flag to enable debug logging on the backend
   * @param {string} [yearFrom] - Optional starting year for date range filter
   * @param {string} [yearTo] - Optional ending year for date range filter
   * @param {string} [rootId] - Optional root ID to search by
   * @param {string} [gameDate] - Optional game date for sports events
   * @param {string} [season] - Optional season for sports events
   * @returns {Promise} - Promise that resolves to search results object (including data and pagination info if available)
   */
  search: async (query, programType, page, limit, genreIds, descLangs, titleLangs, isCastMember, isCrewMember, isDebug, yearFrom, yearTo, rootId, gameDate, season, searchMode) => {
    // If rootId is provided, 'query' might be empty or ignored by backend, so primary validation is on rootId or query length.
    if (rootId) {
      // If rootId is provided, query length check is less critical as rootId is the primary identifier
    } else if (!query || query.length < 2) {
      // Return structure expected by paginated results page (empty data, zero total)
      return { data: [], total: 0, page: 1, limit: limit || 10, totalPages: 0 };
    }
    
    try {
      // Build the query string
      let queryString = '/search?';
      const params = new URLSearchParams();

      if (rootId) {
        params.append('rootId', rootId);
        // 'value' (query) will be ignored by backend if rootId is present,
        // but we can still pass programType for filtering within rootId results.
      } else if (query) {
        params.append('value', query);
      }
      
      // Add program type parameter if provided and not 'ALL'
      // For rootId search, programType can act as a filter on the results
      if (programType && programType !== 'ALL') {
        params.append('programType', programType);
      }

      // Add pagination parameters if provided
      if (page && limit) {
        params.append('page', page.toString());
        params.append('limit', limit.toString());
      }

      // Add genre IDs parameter if provided and not empty
      if (genreIds && genreIds.length > 0) {
        params.append('genreIds', genreIds.join(',')); // Send as comma-separated string
      }
      
      // Add description language parameter if provided and not empty
      if (descLangs && descLangs.length > 0) {
        params.append('descLangs', descLangs.join(','));
      }
      
      // Add title language parameter if provided and not empty
      if (titleLangs && titleLangs.length > 0) {
        params.append('titleLangs', titleLangs.join(','));
      }
      
      // Add year range parameters if provided
      if (yearFrom) {
        params.append('yearFrom', yearFrom);
      }
      
      if (yearTo) {
        params.append('yearTo', yearTo);
      }

      if (gameDate) {
        params.append('gameDate', gameDate);
      }

      if (season) {
        params.append('season', season);
      }
      
      // Add cast member parameter if true
      if (isCastMember) {
        params.append('isCastMember', 'true');
      }
      
      // Add crew member parameter if true
      if (isCrewMember) {
        params.append('isCrewMember', 'true');
      }
      
      // Add debug parameter if true
      if (isDebug) {
        params.append('debug', 'true');
      }

      // Add search mode parameter if provided (exact or startsWith)
      if (searchMode) {
        params.append('searchMode', searchMode);
      }

      queryString += params.toString();
      const response = await apiClient.get(queryString);
      
      // Assume the API response for paginated search will include:
      // - data: array of results for the current page
      // - total: total number of results matching the query
      // - page: current page number
      // - limit: results per page
      // - totalPages: total number of pages
      // If the API returns just the array (non-paginated), adapt as needed.
      
      // Check if the response looks like the paginated structure
      if (response.data && typeof response.data === 'object' && Array.isArray(response.data.data)) {
         // Log performance metrics if available
         if (response.data.performance_metrics) {
          console.debug('Search performance metrics:', response.data.performance_metrics);
        }
        // Return the full response object for pagination handling
        return response.data; 
      } else if (Array.isArray(response.data)) {
        // Handle case where API returns only the data array (non-paginated)
        // Adapt it to the expected paginated structure for consistency
        console.warn('Search API returned an array instead of a paginated object. Adapting response.');
        return {
          data: response.data,
          total: response.data.length, // Assume total is just the length of the returned array
          page: 1, // Assume page 1
          limit: response.data.length, // Assume limit is the length
          totalPages: 1 // Assume only one page
        };
      }
      
      // Fallback for unexpected response format
      console.warn('Unexpected search API response format:', response.data);
      return { data: [], total: 0, page: 1, limit: limit || 10, totalPages: 0 };
    } catch (error) {
      console.error('Search API error:', error);
      throw error;
    }
  },

  /**
   * Get all distinct description languages
   * @returns {Promise} - Promise that resolves to an array of description language strings
   */
  getAllDescLangs: async () => {
    try {
      const response = await apiClient.get('/description/lang/all');
      return response.data; // Assuming response.data is an array of strings
    } catch (error) {
      console.error('Get all description languages API error:', error);
      throw error;
    }
  },

  /**
   * Get all distinct title languages
   * @returns {Promise} - Promise that resolves to an array of title language strings
   */
  getAllTitleLangs: async () => {
    try {
      const response = await apiClient.get('/title/lang/all');
      return response.data; // Assuming response.data is an array of strings
    } catch (error) {
      console.error('Get all title languages API error:', error);
      throw error;
    }
  },
  
  /**
   * Get all genres
   * @returns {Promise} - Promise that resolves to an array of all genres
   */
  getAllGenres: async () => {
    try {
      const response = await apiClient.get('/genres/all');
      return response.data;
    } catch (error) {
      console.error('Get all genres API error:', error);
      throw error;
    }
  },
  
  /**
   * Get program details by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to program details object
   */
  getProgramDetails: async (tmsId) => {
    try {
      const response = await apiClient.get(`/program/${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Program details API error:', error);
      throw error;
    }
  },

  /**
   * Get cast members for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to an array of cast members
   */
  getCast: async (tmsId) => {
    try {
      const response = await apiClient.get(`/cast?tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Cast API error:', error);
      throw error;
    }
  },
  
  /**
   * Get crew members for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to an array of crew members
   */
  getCrew: async (tmsId) => {
    try {
      const response = await apiClient.get(`/crew?tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Crew API error:', error);
      throw error;
    }
  },
  
  /**
   * Get images for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @param {number} width - Optional width parameter for image URL (default: 480)
   * @returns {Promise} - Promise that resolves to an array of images
   */
  getImages: async (tmsId, width = 480) => {
    try {
      const response = await apiClient.get(`/images?tmsid=${tmsId}&width=${width}`);
      return response.data;
    } catch (error) {
      console.error('Images API error:', error);
      throw error;
    }
  },

  /**
   * Get images for a season by season TMS ID
   * @param {string} seasonTMSId - The season's TMS ID
   * @returns {Promise} - Promise that resolves to an array of images
   */
  getSeasonImages: async (seasonTMSId) => {
    try {
      const response = await apiClient.get(`/season-images?seasonTMSId=${seasonTMSId}`);
      return response.data;
    } catch (error) {
      console.error('Season images API error:', error);
      throw error;
    }
  },

    /**
   * Get titles for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to an array of titles
   */
    getTitles: async (tmsId) => {
      try {
        const response = await apiClient.get(`/titles?tmsid=${tmsId}`);
        return response.data;
      } catch (error) {
        console.error('Titles API error:', error);
        throw error;
      }
    },
    
    /**
     * Get descriptions for a program by TMS ID
     * @param {string} tmsId - The TMS ID of the program
     * @returns {Promise} - Promise that resolves to an array of descriptions
     */
    getDescriptions: async (tmsId) => {
      try {
        const response = await apiClient.get(`/descriptions?tmsid=${tmsId}`);
        return response.data;
      } catch (error) {
        console.error('Descriptions API error:', error);
        throw error;
      }
    },

      /**
   * Get releases for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to an array of releases
   */
  getReleases: async (tmsId) => {
    try {
      const response = await apiClient.get(`/releases?tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Releases API error:', error);
      throw error;
    }
  },

    /**
   * Get ratings for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to an array of ratings
   */
    getRatings: async (tmsId) => {
      try {
        const response = await apiClient.get(`/ratings?tmsid=${tmsId}`);
        return response.data;
      } catch (error) {
        console.error('Ratings API error:', error);
        throw error;
      }
    },

      /**
   * Get awards for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to an array of awards
   */
  getAwards: async (tmsId) => {
    try {
      const response = await apiClient.get(`/awards?tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Awards API error:', error);
      throw error;
    }
  },

    /**
   * Get overview data for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to overview data object
   */
    getOverview: async (tmsId) => {
      try {
        const response = await apiClient.get(`/overview?tmsid=${tmsId}`);
        return response.data;
      } catch (error) {
        console.error('Overview API error:', error);
        throw error;
      }
    },

        /**
   * Get related data for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to overview data object
   */
        getRelated: async (tmsId) => {
          try {
            const response = await apiClient.get(`/related?tmsid=${tmsId}`);
            return response.data;
          } catch (error) {
            console.error('Related API error:', error);
            throw error;
          }
        },

    /**
     * Get seasons data for a program by TMS ID
     * @param {string} tmsId - The TMS ID of the program
     * @returns {Promise} - Promise that resolves to an array of seasons
     */
    getSeasons: async (tmsId) => {
      try {
        const response = await apiClient.get(`/seasons?tmsid=${tmsId}`);
        return response.data;
      } catch (error) {
        console.error('Seasons API error:', error);
        throw error;
      }
    },

    /**
     * Get episodes for a specific season by season ID and connector ID
     * @param {string} seasonId - The season ID
     * @param {string} connectorId - The connector ID (tmsid)
     * @returns {Promise} - Promise that resolves to an array of episodes
     */
  getSeasonEpisodes: async (seasonId, connectorId) => {
    try {
      const response = await apiClient.get(`/season_episodes?seasonid=${seasonId}&connectorid=${connectorId}`);
      return response.data;
    } catch (error) {
      console.error('Season episodes API error:', error);
      throw error;
    }
  },

      /**
     * Get episodes for a specific season by connector ID that are not linked to a series
     * @param {string} connectorId - The connector ID (tmsid)
     * @returns {Promise} - Promise that resolves to an array of episodes
     */
  getSeasonEpisodesNoSeries: async (seasonId, connectorId) => {
    try {
      const response = await apiClient.get(`/season_episodes?connectorid=${connectorId}`);
      return response.data;
    } catch (error) {
      console.error('Season episodes API error:', error);
      throw error;
    }
  },

  /**
   * Get video descriptors for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to an array of descriptors
   */
  getDescriptors: async (tmsId) => {
    try {
      const response = await apiClient.get(`/descriptors?tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Descriptors API error:', error);
      throw error;
    }
  },

  /**
   * Get the number of scheduled airings for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to the number of scheduled airings
   */
  getNumScheduled: async (tmsId) => {
    try {
      const response = await apiClient.get(`/numscheduled?tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('NumScheduled API error:', error);
      throw error;
    }
  },

  /**
   * Get scheduled airings for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @param {number} timezoneId - Optional timezone ID for time conversion (defaults to 0 for UTC)
   * @returns {Promise} - Promise that resolves to an object with scheduled airings data and timezone information
   */
  getScheduled: async (tmsId, timezoneId = 0) => {
    try {
      const params = new URLSearchParams();
      params.append('tmsid', tmsId);
      if (timezoneId) {
        params.append('timezone_id', timezoneId);
      }
      const response = await apiClient.get(`/scheduled?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Scheduled airings API error:', error);
      throw error;
    }
  },

  /**
   * Get the number of availabilities for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to the number of availabilities
   */
  getNumAvailabilities: async (tmsId) => {
    try {
      const response = await apiClient.get(`/numavailabilities?tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('NumAvailabilities API error:', error);
      throw error;
    }
  },

  /**
   * Get availabilities for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to an array of availabilities
   */
  getAvailabilities: async (tmsId) => {
    try {
      const response = await apiClient.get(`/availabilities?tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Availabilities API error:', error);
      throw error;
    }
  },

  /**
   * Get versions for a program by root ID
   * @param {string} rootId - The root ID of the program
   * @returns {Promise} - Promise that resolves to an array of versions
   */
  getVersions: async (rootId) => {
    try {
      const response = await apiClient.get(`/versions?rootid=${rootId}`);
      return response.data;
    } catch (error) {
      console.error('Versions API error:', error);
      throw error;
    }
  },

  /**
   * Get sports information for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to sports information object
   */
  getSportsInfo: async (tmsId) => {
    try {
      const response = await apiClient.get(`/spinfo?tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Sports info API error:', error);
      throw error;
    }
  },

  /**
   * Get sports genres
   * @returns {Promise} - Promise that resolves to an array of sports genres
   */
  getSportsGenres: async () => {
    try {
      const response = await apiClient.get('/sporgs');
      return response.data;
    } catch (error) {
      console.error('Sports genres API error:', error);
      throw error;
    }
  },

  /**
   * Get sports organizations by genre ID
   * @param {number} genreId - The genre ID
   * @returns {Promise} - Promise that resolves to an array of sports organizations
   */
  getSportsOrganizations: async (genreId) => {
    try {
      const response = await apiClient.get(`/sporg?genreid=${genreId}`);
      return response.data;
    } catch (error) {
      console.error('Sports organizations API error:', error);
      throw error;
    }
  },

  /**
   * Get conferences for a sports organization by organization ID
   * @param {number} organizationId - The organization ID
   * @returns {Promise} - Promise that resolves to an array of conferences
   */
  getSportsConferences: async (organizationId) => {
    try {
      const response = await apiClient.get(`/spconference?organizationid=${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Sports conferences API error:', error);
      throw error;
    }
  },

  /**
   * Get sports connectors by root ID
   * @param {number} rootId - The root ID
   * @returns {Promise} - Promise that resolves to an array of sports connectors
   */
  getSportsConnectors: async (rootId) => {
    try {
      const response = await apiClient.get(`/spconnectors?rootid=${rootId}`);
      return response.data;
    } catch (error) {
      console.error('Sports connectors API error:', error);
      throw error;
    }
  },

  /**
   * Get sports organization details by organization ID
   * @param {number} organizationId - The organization ID
   * @returns {Promise} - Promise that resolves to organization details including assets
   */
  getSportsOrganizationDetails: async (organizationId) => {
    try {
      const response = await apiClient.get(`/sporganization?organizationid=${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Sports organization details API error:', error);
      throw error;
    }
  },

  /**
   * Get distinct seasons for programs with a specified connector ID
   * @param {string} connectorId - The connector ID
   * @returns {Promise} - Promise that resolves to an object with seasons and program count
   */
  getSeasonsForConnector: async (connectorId) => {
    try {
      const response = await apiClient.get(`/getseasonsforconnector?connectorid=${connectorId}`);
      return response.data;
    } catch (error) {
      console.error('Seasons for connector API error:', error);
      throw error;
    }
  },

  /**
   * Get programs for a specific connector ID and season
   * @param {string} connectorId - The connector ID
   * @param {string} season - The season
   * @param {object} [params] - Optional parameters for pagination and sorting (e.g., { page, limit, sort_by, sort_order })
   * @returns {Promise} - Promise that resolves to an object containing programs and pagination info
   */
  getProgramsBySeason: async (connectorId, season, params = {}) => {
    try {
      // Start with base parameters
      const queryParams = {
        connectorid: connectorId,
        season: season,
        // Add pagination/sorting params if they exist in the params object
        ...(params.page && { page: params.page }),
        ...(params.limit && { limit: params.limit }),
        ...(params.sort_by && { sort_by: params.sort_by }),
        ...(params.sort_order && { sort_order: params.sort_order }),
      };
      
      // Axios will automatically handle URL encoding for params object
      const response = await apiClient.get(`/programsbyseason`, { params: queryParams });
      
      // Expecting response.data to be { programs: [...], pagination: {...} }
      return response.data; 
    } catch (error) {
      console.error('Programs by season API error:', error);
      throw error;
    }
  },

  /**
   * Search for celebrities by name
   * @param {object} params - Parameters for the search (e.g., { query, debug })
   * @returns {Promise} - Promise that resolves to an array of celebrities
   */
  searchCelebrities: async (params = {}) => {
    const { query, debug } = params;
    if (!query || query.length < 2) {
      return { data: [] };
    }
    
    try {
      const requestParams = { name: query };
      
      // Add debug parameter if true
      if (debug) {
        requestParams.debug = 'true';
      }
      
      const response = await apiClient.get(`/celebrities/search`, { params: requestParams });
      
      // Log performance metrics if available
      if (response.data && response.data.performance_metrics) {
        console.debug('Celebrity search performance metrics:', response.data.performance_metrics);
      }
      
      return response.data;
    } catch (error) {
      console.error('Celebrity search API error:', error);
      throw error;
    }
  },

  /**
   * Get celebrity details by person ID
   * @param {number} personId - The person ID of the celebrity
   * @returns {Promise} - Promise that resolves to celebrity details
   */
  getCelebrityDetails: async (personId) => {
    try {
      const response = await apiClient.get(`/celebrities/${personId}`);
      return response.data;
    } catch (error) {
      console.error('Celebrity details API error:', error);
      throw error;
    }
  },

  /**
   * Browse celebrities with pagination, sorting, and optional search.
   * @param {object} params - Parameters for pagination, sorting, and search (e.g., { page, limit, sort_by, sort_order, search })
   * @returns {Promise} - Promise that resolves to an object containing celebrity data and pagination info
   */
  browseCelebrities: async (params = {}) => {
    try {
      // Add a timestamp to prevent browser caching
      const paramsWithTimestamp = {
        ...params,
        _t: Date.now()
      };
      
      // If a search term is provided, add it to the parameters
      if (params.search) {
        paramsWithTimestamp.search = params.search;
      }

      // Axios automatically handles URL encoding for the params object
      const response = await apiClient.get('/celebrities/browse', { params: paramsWithTimestamp });
      
      // Log the actual URL being requested for debugging
      console.debug('Celebrity Browse URL:', apiClient.defaults.baseURL + '/celebrities/browse', paramsWithTimestamp);
      
      // Expecting response.data to be { data: [...], pagination: {...}, performance_metrics: {...} }
      if (response.data && response.data.performance_metrics) {
        console.debug('Celebrity Browse performance metrics:', response.data.performance_metrics);
      }
      return response.data;
    } catch (error) {
      console.error('Celebrity browse API error:', error);
      throw error;
    }
  },

  /**
   * Get episode details by TMS ID (specifically for Sports Events)
   * @param {string} tmsId - The TMS ID of the episode (program)
   * @returns {Promise} - Promise that resolves to episode details object or array
   */
  getEpisodeDetails: async (tmsId) => {
    try {
      // The example shows the endpoint returning an array, even for a single ID
      const response = await apiClient.get(`/episodes/${tmsId}`);
      // Return the first element if it's an array, otherwise the data itself
      return Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : response.data;
    } catch (error) {
      // It's possible an episode detail doesn't exist for a sports event TMSID
      // Log as warning instead of error, and return null
      if (error.response && error.response.status === 404) {
        console.warn(`Episode details not found for TMSID ${tmsId}:`, error.message);
        return null;
      }
      console.error('Episode details API error:', error);
      throw error; // Re-throw other errors
    }
  },

  /**
   * Compare programs by TMS IDs
   * @param {string} tmsids - Comma-separated string of TMS IDs
   * @returns {Promise} - Promise that resolves to comparison data object
   */
  comparePrograms: async (tmsids) => {
    try {
      const response = await apiClient.get(`/compare`, { params: { tmsids } });
      return response.data;
    } catch (error) {
      console.error(`Error comparing programs with TMS IDs ${tmsids}:`, error);
      throw error;
    }
  },

  // Fetch children programs for a connector ID (paginated)
  getChildren: async (params) => {
    try {
      // Use apiClient instance which includes interceptors and base URL
      const response = await apiClient.get(`/children`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching children programs:', error);
      throw error; // Re-throw to be caught by the component
    }
  },

  // Fetch all children programs for export (no pagination, full dataset)
  exportChildren: async (connectorId, sortBy, sortOrder) => {
    try {
      const params = {
        connectorid: connectorId,
      };
      if (sortBy) {
        params.sort_by = sortBy;
      }
      if (sortOrder) {
        params.sort_order = sortOrder;
      }
      const response = await apiClient.get(`/children/export`, { params });
      return response.data; // Expecting an array of programs
    } catch (error) {
      console.error('Error exporting children programs:', error);
      throw error;
    }
  },

  // Settings API methods
  checkSavedItem: async (userId, tmsId) => {
    try {
      if (!userId || !tmsId) {
        return { saved: false };
      }
      const response = await apiClient.get(`/settings/saved/check?userid=${userId}&tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Check saved item API error:', error);
      return { saved: false }; // Default to false on error
    }
  },

  getSavedItems: async (userId, page = 1, limit = 10, tmsId = null) => {
    try {
      const params = new URLSearchParams();
      params.append('userid', userId);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (tmsId) {
        params.append('tmsid', tmsId);
      }
      const response = await apiClient.get(`/settings/saved?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get saved items API error:', error);
      throw error;
    }
  },

  saveItem: async (userId, tmsId) => {
    try {
      const response = await apiClient.post('/settings/saved', {
        userid: userId,
        tmsid: tmsId
      });
      return response.data;
    } catch (error) {
      console.error('Save item API error:', error);
      throw error;
    }
  },

  updateSavedItem: async (userId, tmsId, newTmsId) => {
    try {
      const response = await apiClient.put('/settings/saved', {
        userid: userId,
        tmsid: tmsId,
        new_tmsid: newTmsId
      });
      return response.data;
    } catch (error) {
      console.error('Update saved item API error:', error);
      throw error;
    }
  },

  deleteSavedItem: async (userId, tmsId) => {
    try {
      const response = await apiClient.delete(`/settings/saved?userid=${userId}&tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Delete saved item API error:', error);
      throw error;
    }
  },

  getUserSettings: async (userId) => {
    try {
      const response = await apiClient.get(`/settings/user?userid=${userId}`);
      return response.data;
    } catch (error) {
      console.error('Get user settings API error:', error);
      throw error;
    }
  },

  saveUserSetting: async (userId, key, value) => {
    try {
      const response = await apiClient.post('/settings/user', {
        userid: userId,
        key: key,
        value: value
      });
      return response.data;
    } catch (error) {
      console.error('Save user setting API error:', error);
      throw error;
    }
  },

  /**
   * Get Telus VOD assets with pagination and optional title and asset type filtering.
   * @param {number} page - The page number for pagination (1-based).
   * @param {number} limit - The number of results per page.
   * @param {string} title - Optional title filter string.
   * @param {string} assetType - Optional asset type filter string (SVOD or TVOD).
   * @returns {Promise} - Promise that resolves to an object containing VOD assets data and pagination info.
   */
  getTelusVODAssets: async (page = 1, limit = 20, title = null, assetType = null) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (title) {
        params.append('title', title);
      }
      if (assetType) {
        params.append('assetType', assetType);
      }
      const response = await apiClient.get(`/telus/browse?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Telus VOD assets API error:', error);
      throw error;
    }
  },

  /**
   * Get Encore AI data for a program by TMS ID
   * @param {string} tmsId - The TMS ID of the program
   * @returns {Promise} - Promise that resolves to Encore AI data object
   */
  getEncoreAI: async (tmsId) => {
    try {
      const response = await apiClient.get(`/encoreai?tmsid=${tmsId}`);
      return response.data;
    } catch (error) {
      console.error('Encore AI API error:', error);
      throw error;
    }
  },

  /**
   * Get similar programs using Encore AI vector similarity
   * @param {string} tmsId - The TMS ID of the program to find similar programs for
   * @param {number} limit - Optional number of similar programs to return (default: 10, max: 50)
   * @returns {Promise} - Promise that resolves to similar programs data object
   */
  getSimilarPrograms: async (tmsId, limit = 10) => {
    try {
      const params = new URLSearchParams();
      params.append('tmsid', tmsId);
      if (limit) {
        params.append('limit', limit.toString());
      }
      const response = await apiClient.get(`/encoreai/similar?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Similar programs API error:', error);
      throw error;
    }
  },

  /**
   * Change password for local authentication users
   * @param {string} currentPassword - The user's current password
   * @param {string} newPassword - The new password
   * @param {string} confirmPassword - Confirmation of the new password
   * @returns {Promise} - Promise that resolves to success response
   */
  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await apiClient.post('/auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword
      });
      return response.data;
    } catch (error) {
      console.error('Change password API error:', error);
      throw error;
    }
  },

  /**
   * Get all available timezones
   * @returns {Promise} - Promise that resolves to an array of timezone objects
   */
  getTimezones: async () => {
    try {
      const response = await apiClient.get('/timezones');
      return response.data;
    } catch (error) {
      console.error('Get timezones API error:', error);
      throw error;
    }
  },

  /**
   * Get the current user's MCP API key
   * @returns {Promise} - Promise that resolves to MCP key object
   */
  getMcpKey: async () => {
    try {
      const response = await apiClient.get('/mcp/key');
      return response.data;
    } catch (error) {
      console.error('Get MCP key API error:', error);
      throw error;
    }
  },

  /**
   * Generate a new MCP API key
   * @returns {Promise} - Promise that resolves to success response with new key
   */
  generateMcpKey: async () => {
    try {
      const response = await apiClient.post('/mcp/key/generate');
      return response.data;
    } catch (error) {
      console.error('Generate MCP key API error:', error);
      throw error;
    }
  },

  /**
   * Deactivate the current MCP API key
   * @returns {Promise} - Promise that resolves to success response
   */
  deactivateMcpKey: async () => {
    try {
      const response = await apiClient.post('/mcp/key/deactivate');
      return response.data;
    } catch (error) {
      console.error('Deactivate MCP key API error:', error);
      throw error;
    }
  },

  /**
   * Fetch upstream Gracenote program data by TMS ID.
   * The backend derives the API key from the user's carrier.
   * @param {string} tmsId - The TMS ID to look up
   * @returns {Promise<string>} - Promise that resolves to the raw upstream response text (XML)
   */
  getUpstreamPrograms: async (tmsId) => {
    try {
      const response = await apiClient.get('/upstream/programs', {
        params: { tmsId },
        responseType: 'text',
        transformResponse: [(data) => data],
      });
      return response.data;
    } catch (error) {
      console.error('Upstream programs API error:', error);
      throw error;
    }
  }
};

/**
 * Updates the base URL for the shared axios instance.
 * @param {string} newUrl - The new base URL to use for API requests.
 */
export const updateApiClientBaseUrl = (newUrl) => {
  if (newUrl && typeof newUrl === 'string') {
    apiClient.defaults.baseURL = newUrl;
    console.log(`API client base URL updated to: ${newUrl}`);
  } else {
    console.error('Invalid URL provided for API client base URL update:', newUrl);
  }
};

export default apiService;
